import { createUfoDraft, resizeUfoDraft } from '../templates/ufo-adventure/domain/editorModel';
import { createLaneRallyDraft, resizeLaneRallyDraft } from '../templates/lane-rally/domain/editorModel';
import { createStationRunDraft, resizeStationRunDraft } from '../templates/station-run/domain/editorModel';
import type { DraftInput } from '../../shared/drafts';
import type { TemplateKey } from '../../shared/templates';
import { createFieryRouteDraft, resizeFieryRouteDraft } from '../templates/fiery-route/domain/editorModel';
import { createOrbitalBridgeDraft, resizeOrbitalBridgeDraft } from '../templates/orbital-bridge/domain/editorModel';
import { createTurboDuelDraft, resizeTurboDuelDraft } from '../templates/turbo-duel/domain/editorModel';
import { createBalanceArenaDraft, resizeBalanceArenaDraft } from '../templates/balance-arena/domain/editorModel';
import { createBasketLearningDraft, resizeBasketLearningDraft } from '../templates/basket-learning/domain/editorModel';
import { createGoalArenaDraft, resizeGoalArenaDraft } from '../templates/goal-arena/domain/editorModel';
import { createBillionaireQuizDraft, resizeBillionaireQuizDraft } from '../templates/billionaire-quiz/domain/editorModel';

export function createStarterDraft(templateKey: TemplateKey = 'train-adventure'): DraftInput {
  if (templateKey === 'station-run') return createStationRunDraft();
  if (templateKey === 'lane-rally') return createLaneRallyDraft();
  if (templateKey === 'ufo-adventure') return createUfoDraft();
  if (templateKey === 'fiery-route') return createFieryRouteDraft();
  if (templateKey === 'orbital-bridge') return createOrbitalBridgeDraft();
  if (templateKey === 'turbo-duel') return createTurboDuelDraft();
  if (templateKey === 'balance-arena') return createBalanceArenaDraft();
  if (templateKey === 'basket-learning') return createBasketLearningDraft();
  if (templateKey === 'goal-arena') return createGoalArenaDraft();
  if (templateKey === 'billionaire-quiz') return createBillionaireQuizDraft();
  return {
    title: 'Yeni Tren Macerası',
    templateKey: 'train-adventure',
    levelMode: 'manual',
    maxLevels: 1,
    trackTime: true,
    questions: [createQuestion(1, 1)],
  };
}

function createQuestion(level: number, questionNumber: number): DraftInput['questions'][number] {
  return {
    prompt: `${questionNumber}. soru`,
    wagonStyle: 'wide',
    levels: [level],
    choices: [
      { text: 'Doğru cevap', isCorrect: true },
      { text: 'Yanlış cevap 1', isCorrect: false },
      { text: 'Yanlış cevap 2', isCorrect: false },
    ],
  };
}

export function questionsForLevel(draft: DraftInput, level: number) {
  return draft.questions
    .map((question, questionIndex) => ({ question, questionIndex }))
    .filter(({ question }) => question.levels[0] === level);
}

export function reconfigureDraft(
  _draft: DraftInput,
  _maxLevels: number,
): DraftInput {
  if (_draft.templateKey === 'station-run') return resizeStationRunDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'lane-rally') return resizeLaneRallyDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'ufo-adventure') return resizeUfoDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'fiery-route') return resizeFieryRouteDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'orbital-bridge') return resizeOrbitalBridgeDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'turbo-duel') return resizeTurboDuelDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'balance-arena') return resizeBalanceArenaDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'basket-learning') return resizeBasketLearningDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'goal-arena') return resizeGoalArenaDraft(_draft, _maxLevels);
  if (_draft.templateKey === 'billionaire-quiz') return resizeBillionaireQuizDraft(_draft, _maxLevels);
  const safeMaxLevels = Math.max(1, Math.min(5, Math.trunc(_maxLevels)));
  const questions = Array.from({ length: safeMaxLevels }, (_, levelIndex) => {
    const level = levelIndex + 1;
    const existing = questionsForLevel(_draft, level);
    return Array.from({ length: level }, (_unused, questionIndex) => ({
      ...(existing[questionIndex]?.question ?? createQuestion(level, questionIndex + 1)),
      levels: [level],
    }));
  }).flat();
  return {
    ..._draft,
    levelMode: 'manual',
    maxLevels: safeMaxLevels,
    questions,
  };
}

export function removedQuestionCount(draft: DraftInput, nextMaxLevels: number): number {
  return draft.questions.filter((question) => question.levels[0] > nextMaxLevels).length;
}

export function isQuestionReady(question: DraftInput['questions'][number]): boolean {
  return Boolean(
    (question.prompt.trim() || question.mediaId) &&
    question.choices.length >= 2 &&
    question.choices.filter((choice) => choice.isCorrect).length === 1 &&
    question.choices.every((choice) => choice.text.trim() || choice.mediaId),
  );
}
